import React, { useMemo, useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { __resolve } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { DatePicker } from '@/components/ui/DatePicker/DatePicker';
import { MonthView } from './MonthView';
import { WeekView } from './WeekView';
import { DayView } from './DayView';
import { AgendaView } from './AgendaView';
import type { CalendarEvent, CalendarViewMode } from './types';
import { formatDate, getLocaleSettings, getMonthNames, getMonthShortNames, getWeekdayLongNames } from '@/lib/localeFormatters';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';

export interface CalendarProps {
  initialDate?: Date;
  initialView?: CalendarViewMode;
  events?: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  /** Optional callback when a day cell is clicked (month view). */
  onDayClick?: (date: Date) => void;
  className?: string;
  onRangeChange?: (from: Date, to: Date, view: CalendarViewMode) => void;
  /** Optional custom renderer for each calendar event chip. */
  eventRenderer?: (event: CalendarEvent) => React.ReactNode;
}

// Removed static English arrays — now using locale-aware getMonthNames(), getMonthShortNames(), getWeekdayLongNames()

function startOfWeek(date: Date, firstDay: 0 | 1 = 1) {
  const d = new Date(date);
  const diff = (d.getDay() + 7 - firstDay) % 7;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(d: Date) {
  const e = new Date(d);
  e.setHours(23,59,59,999);
  return e;
}

function monthRange(date: Date) {
  const from = new Date(date.getFullYear(), date.getMonth(), 1);
  const to = endOfDay(new Date(date.getFullYear(), date.getMonth() + 1, 0));
  return { from, to };
}

function weekRange(date: Date) {
  const from = startOfWeek(date);
  const to = endOfDay(new Date(from.getTime() + 6 * 24 * 60 * 60 * 1000));
  return { from, to };
}

function dayRange(date: Date) {
  const from = new Date(date);
  from.setHours(0,0,0,0);
  const to = endOfDay(from);
  return { from, to };
}

export const Calendar: React.FC<CalendarProps> = ({
  initialDate = new Date(),
  initialView = 'month',
  events,
  onEventClick,
  onDayClick,
  className,
  onRangeChange,
  eventRenderer,
}) => {
  const [activeDate, setActiveDate] = useState<Date>(initialDate);
  const [view, setView] = useState<CalendarViewMode>(initialView);
  const rangeCbRef = useRef<CalendarProps['onRangeChange'] | null>(null);

  // Subscribe to locale store for reactive re-renders when settings change
  const localeSettings = useLocaleSettings();

  // Keep latest callback in a ref to avoid effect churn on identity changes
  useEffect(() => { rangeCbRef.current = onRangeChange; }, [onRangeChange]);

  // Notify range changes
  useEffect(() => {
    const cb = rangeCbRef.current;
    if (!cb) return;
    let from: Date, to: Date;
    if (view === 'month') ({ from, to } = monthRange(activeDate));
    else if (view === 'week') ({ from, to } = weekRange(activeDate));
    else if (view === 'day') ({ from, to } = dayRange(activeDate));
    else ({ from, to } = weekRange(activeDate));
    cb(from, to, view);
  }, [activeDate, view]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key.toLowerCase() === 't') goToday();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeDate, view]);

  const title = useMemo(() => {
    const monthNames = getMonthNames();
    const monthShort = getMonthShortNames();
    const weekdayLong = getWeekdayLongNames();
    if (view === 'month') return `${monthNames[activeDate.getMonth()]} ${activeDate.getFullYear()}`;
    if (view === 'week') {
      const s = startOfWeek(activeDate);
      const e = new Date(s.getTime() + 6 * 24 * 60 * 60 * 1000);
      return `${monthShort[s.getMonth()]} ${s.getDate()} - ${monthShort[e.getMonth()]} ${e.getDate()}`;
    }
    if (view === 'day') return `${weekdayLong[activeDate.getDay()]}, ${monthShort[activeDate.getMonth()]} ${activeDate.getDate()}`;
    return __resolve('ui::Agenda');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDate, view, localeSettings.dateFormat]);

  const goToday = () => {
    setActiveDate(new Date());
  };
  const goPrev = () => {
    if (view === 'month') {
      setActiveDate(new Date(activeDate.getFullYear(), activeDate.getMonth() - 1, 1));
      return;
    }
    const d = new Date(activeDate);
    if (view === 'week') d.setDate(d.getDate() - 7);
    else if (view === 'day') d.setDate(d.getDate() - 1);
    else if (view === 'agenda') d.setDate(d.getDate() - 7);
    setActiveDate(d);
  };
  const goNext = () => {
    if (view === 'month') {
      setActiveDate(new Date(activeDate.getFullYear(), activeDate.getMonth() + 1, 1));
      return;
    }
    const d = new Date(activeDate);
    if (view === 'week') d.setDate(d.getDate() + 7);
    else if (view === 'day') d.setDate(d.getDate() + 1);
    else if (view === 'agenda') d.setDate(d.getDate() + 7);
    setActiveDate(d);
  };

  return (
    <div className={cn("w-full space-y-3", className)}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goPrev} aria-label="Previous" data-testid="calendar-nav-prev">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>
            {__resolve('ui::Today')}
          </Button>
          <Button variant="outline" size="sm" onClick={goNext} aria-label="Next" data-testid="calendar-nav-next">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
          <div className="ml-2 text-lg font-semibold">{title}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">{__resolve('ui::Jump to date')}:</span>
          <DatePicker
            aria-label="Select date"
            value={activeDate}
            onChange={(date) => {
              if (date) {
                setActiveDate(date);
              }
            }}
          />
          <Select
            aria-label="Select view"
            value={view}
            onChange={(e) => setView(e.target.value as CalendarViewMode)}
          >
            <option value="month">{__resolve('ui::Month')}</option>
            <option value="week">{__resolve('ui::Week')}</option>
            <option value="day">{__resolve('ui::Day')}</option>
            {/* Agenda view is intended for Upcoming lists; omitted from main calendar switcher */}
          </Select>
        </div>
      </div>

      {/* Body */}
      {view === 'month' && <MonthView month={activeDate} events={events} onEventClick={onEventClick} onDayClick={onDayClick} eventRenderer={eventRenderer} />}
      {/* W8-5: pass eventRenderer to WeekView and DayView so MeetingChip (with its own
          click handler and MeetingEditModal) is used in all views, not just MonthView. */}
      {view === 'week' && <WeekView weekStart={activeDate} events={events} onEventClick={onEventClick} eventRenderer={eventRenderer} />}
      {view === 'day' && <DayView day={activeDate} events={events} onEventClick={onEventClick} eventRenderer={eventRenderer} />}
      {view === 'agenda' && (() => {
        const { from, to } = weekRange(activeDate);
        return (
          <AgendaView
            from={from}
            to={to}
            events={events}
            onEventClick={onEventClick}
          />
        );
      })()}
    </div>
  );
};


